import { createReducer } from '@reduxjs/toolkit'

import { DEFAULT_COUNTRY_ISO_CODE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { LyraCodeValidationInfo } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { CustomerInfo } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'
import { Patient } from '@lyrahealth-inc/shared-app-logic/src/models/healthPlan/HealthPlan'
import { ProviderAdminProviderInfo } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { Transfer } from '@lyrahealth-inc/shared-app-logic/src/models/transfer/Transfer'
import { ProgramCoverageBreakdown } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

import {
  CLEAR_LYRA_CODE_VALIDATION_INFO,
  CLEAR_REQUEST_PAYMENT_STORE,
  CLOSE_CANCELLED_SESSIONS_EXCEEDED_MODAL,
  CLOSE_DUPLICATE_PAYMENT_MODAL,
  CLOSE_INELIGIBLE_CLIENT_MODAL,
  CLOSE_OUTCOME_REMINDER_MODAL,
  CLOSE_SESSIONS_EXCEEDED_MODAL,
  CLOSE_SESSIONS_MODAL,
  DISPLAY_INELIGIBLE_CLIENT_MODAL,
  GET_CHARGE_TO_COPY,
  GET_CLIENT,
  GET_CUSTOMER_PROGRAM_COVERAGE_BREAKDOWN,
  GET_CUSTOMER_SESSION_TYPES,
  GET_CUSTOMERS,
  GET_CUSTOMERS_BY_COUNTRY,
  GET_CUSTOMERS_BY_COUNTRY_FAILED,
  GET_PRACTICE_PROVIDERS,
  GET_PROVIDER_RATES,
  SELECT_PROVIDER,
  SET_ELIGIBILITY_STATUS,
  SET_LYRA_CODE_VALIDATION_INFO,
  SET_SELECTED_PROVIDER_CAPACITY,
  SHOW_CANCELLED_SESSIONS_EXCEEDED_MODAL,
  SHOW_DUPLICATE_PAYMENT_MODAL,
  SUBMIT_PAYMENT_REQUEST,
} from '../../common/constants/reduxConstants'

type RequestPaymentState = {
  showDuplicateModal: boolean
  showCancelledSessionsExceededModal: boolean
  rates?: {
    smp_session?: string
    ssc_session?: string
  }
  customers: CustomerInfo[]
  customersByCountry: {
    [key: string]: CustomerInfo[]
  }
  customersByCountryFetchFailed: {
    [key: string]: boolean
  }
  customerProgramCoverageBreakdown?: {
    [country: string]: {
      [customerName: string]: ProgramCoverageBreakdown
    }
  }
  customerSessionTypes?: {
    [customerName: string]: { label: string; value: string }[]
  }
  providersList?: ProviderAdminProviderInfo[]
  copy?: any
  patient?: Patient
  data: {
    session_tracking_status?: string | null
    outcome_reminder_status?: { show_reminder: boolean; outcome_sent_date: string }
    data?: Transfer
  }
  response?: {
    data: {
      session_tracking_status?: string | null
    }
  }
  lyraCodeValidationInfo?: LyraCodeValidationInfo
  eligibility?: {
    status: boolean
  }
  selectedProvider?: ProviderAdminProviderInfo
  showIneligibleClientModal?: boolean
  providerDiffPricingInfo?: {
    differential_pricing_enabled: boolean
    differential_pricing_in_person_modifier: number
  }
}

const processCustomers = (customers: any[]) => {
  // filter out customers that are internal only
  return customers
    .filter((c: any) => !c.internal_customer)
    .map((customer: any) => {
      if (
        customer.display_visits_per_issue_per_year_by_program &&
        typeof customer.display_visits_per_issue_per_year_by_program === 'string'
      ) {
        try {
          customer.display_visits_per_issue_per_year_by_program = JSON.parse(
            customer.display_visits_per_issue_per_year_by_program,
          )
        } catch (error) {
          console.warn('Failed to parse display_visits_per_issue_per_year_by_program:', error)
        }
      }
      // Backend serves this as a JSON string for CustomerInfo; CustomerInfo.sessions_limit_by_program
      // is typed as a structured object, so normalize once here.
      if (customer.sessions_limit_by_program && typeof customer.sessions_limit_by_program === 'string') {
        try {
          customer.sessions_limit_by_program = JSON.parse(customer.sessions_limit_by_program)
        } catch (error) {
          console.warn('Failed to parse sessions_limit_by_program:', error)
          customer.sessions_limit_by_program = undefined
        }
      }
      return customer
    })
}

const initialState = {
  showDuplicateModal: false,
  showCancelledSessionsExceededModal: false,
  data: {},
  customers: [],
  customersByCountry: {},
  customersByCountryFetchFailed: {},
  customerProgramCoverageBreakdown: {},
  customerSessionTypes: {},
}

export default createReducer<RequestPaymentState>(initialState, (builder) => {
  builder.addCase(GET_PROVIDER_RATES, (state, action: any) => {
    state.rates = action.data.data
    state.providerDiffPricingInfo = action.data.provider_diff_pricing_info
    return state
  })
  builder.addCase(SUBMIT_PAYMENT_REQUEST, (state, action: any) => {
    return {
      ...state,
      copy: undefined,
      patient: undefined,
      data: {
        ...state.data,
        ...action.payload.data,
      },
    }
  })
  builder.addCase(CLEAR_REQUEST_PAYMENT_STORE, (state, _: any) => {
    return {
      ...initialState,
      customers: state.customers,
    }
  })
  builder.addCase(`${GET_PRACTICE_PROVIDERS}_FULFILLED`, (state, action: any) => {
    state.providersList = action.payload.data.practice_providers
    return state
  })
  builder.addCase(`${GET_CHARGE_TO_COPY}_FULFILLED`, (state, action: any) => {
    state.copy = action.payload.data
    return state
  })
  builder.addCase(GET_CUSTOMERS, (state, action: any) => {
    const customers = action.data.customers ?? []
    state.customers = processCustomers(customers)
    return state
  })
  builder.addCase(GET_CUSTOMERS_BY_COUNTRY, (state, action: any) => {
    state.customersByCountry[action.country] = processCustomers(action.payload.customers)
    delete state.customersByCountryFetchFailed[action.country]
    return state
  })
  builder.addCase(GET_CUSTOMERS_BY_COUNTRY_FAILED, (state, action: any) => {
    state.customersByCountryFetchFailed[action.country] = true
    return state
  })
  builder.addCase(`${GET_CUSTOMER_PROGRAM_COVERAGE_BREAKDOWN}_FULFILLED`, (state, action: any) => {
    const { customer } = action.meta.arg
    const country = action.meta.arg.country ?? DEFAULT_COUNTRY_ISO_CODE
    if (customer) {
      state.customerProgramCoverageBreakdown = {
        ...state.customerProgramCoverageBreakdown,
        [country]: {
          ...state.customerProgramCoverageBreakdown?.[country],
          [customer]: action.payload.data,
        },
      }
    }
    return state
  })
  builder.addCase(`${GET_CUSTOMER_SESSION_TYPES}_FULFILLED`, (state, action: any) => {
    const customer = action.meta?.arg?.customer

    if (customer) {
      state.customerSessionTypes = {
        ...state.customerSessionTypes,
        [customer]: action.payload.data,
      }
    }
    return state
  })
  builder.addCase(CLOSE_SESSIONS_MODAL, (state, action: any) => {
    state.data.session_tracking_status = action.payload
    return state
  })
  builder.addCase(CLOSE_SESSIONS_EXCEEDED_MODAL, (state, action: any) => {
    state.response = {
      data: {
        session_tracking_status: action.payload,
      },
    }
    return state
  })
  builder.addCase(SHOW_DUPLICATE_PAYMENT_MODAL, (state) => {
    state.showDuplicateModal = true
    return state
  })
  builder.addCase(CLOSE_DUPLICATE_PAYMENT_MODAL, (state) => {
    state.showDuplicateModal = false
    return state
  })
  builder.addCase(SHOW_CANCELLED_SESSIONS_EXCEEDED_MODAL, (state) => {
    state.showCancelledSessionsExceededModal = true
    return state
  })
  builder.addCase(CLOSE_CANCELLED_SESSIONS_EXCEEDED_MODAL, (state) => {
    state.showCancelledSessionsExceededModal = false
    return state
  })
  builder.addCase(DISPLAY_INELIGIBLE_CLIENT_MODAL, (state) => {
    state.showIneligibleClientModal = true
    return state
  })
  builder.addCase(CLOSE_INELIGIBLE_CLIENT_MODAL, (state) => {
    state.showIneligibleClientModal = false
    return state
  })
  builder.addCase(SELECT_PROVIDER, (state, action: any) => {
    const selectedProvider = state.providersList?.find((prov) => {
      return prov.lyra_id === action.payload
    })
    state.selectedProvider = selectedProvider
    return state
  })
  builder.addCase(`${GET_CLIENT}_FULFILLED`, (state, action: any) => {
    state.patient = action.payload.data.clients[0]
    return state
  })
  builder.addCase(CLOSE_OUTCOME_REMINDER_MODAL, (state) => {
    state.data.outcome_reminder_status = undefined
    return state
  })
  builder.addCase(SET_LYRA_CODE_VALIDATION_INFO, (state, action: any) => {
    state.lyraCodeValidationInfo = action.data
    return state
  })
  builder.addCase(CLEAR_LYRA_CODE_VALIDATION_INFO, (state) => {
    state.lyraCodeValidationInfo = undefined
    return state
  })
  builder.addCase(SET_ELIGIBILITY_STATUS, (state, action: any) => {
    state.eligibility = action.data.user_eligibility
    return state
  })
  builder.addCase(`${SET_SELECTED_PROVIDER_CAPACITY}_FULFILLED`, (state, action: any) => {
    if (!state.selectedProvider) {
      return state
    }
    const selectedProvider: ProviderAdminProviderInfo = {
      ...state.selectedProvider,
      capacity_value: action.payload.data.capacity_value,
      available: action.payload.data.available,
      capacity_updated_at: action.payload.data.capacity_updated_at,
    }
    const updatedProviders = state.providersList?.map((provider) => {
      if (provider.id === selectedProvider.id) return selectedProvider
      return provider
    })
    state.selectedProvider = selectedProvider
    state.providersList = updatedProviders
    return state
  })
})
